import { Suspense } from 'react';

import { ReactQueryDevtools } from 'react-query/devtools';
import { ErrorBoundary } from 'react-error-boundary';
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';

import League from '@leagueplatform/core';
import { useOnlineStatus } from '@leagueplatform/web-common';
import { driver, wsDriver } from '@leagueplatform/masonry';
import { RecentClaimsCardList } from '@leagueplatform/claims';
import { ActivityStackWidgetV2 } from '@leagueplatform/health-journey';
import {
  FullPageError,
  LoadingIndicator,
  ErrorState,
} from '@leagueplatform/web-common-components';
import { StandaloneAuthInitConfig } from '@leagueplatform/auth-standalone';
import { PCPCustomSectionRenderer } from 'components/masonry/pcp-custom-section-renderer.component';
import { CustomerServiceCard } from 'components/cards/customer-service-card.component';
import { StatusBannerRenderer } from 'components/masonry/status-banner-renderer.component';
import { ScfhpXSVisualCardRenderer } from 'components/masonry/scfhp-xs-visual-card-renderer.component';
import { ScfhpStatusBannerRenderer } from 'components/masonry/scfhp-status-banner-renderer.component';
import { SuggestedProgramsCarousel } from 'components/carousels/suggested-programs-carousel.component';
import { useGetLeagueConfig } from 'hooks/use-get-league-config.hook';

import { GDSThemeProvider, Spinner } from '@leagueplatform/genesis-core';
import { santaClaraTheme } from 'theme/old-santaClaraLightTheme';
import { AuthorizationCard } from 'components/cards/authorization-card.component';
import { AuthorizationServiceCard } from 'components/cards/authorization-service-card.component';
import { AuthorizationServiceInfoSection } from 'components/cards/authorization-service-info-section.component';
import { AuthorizationDetailsHeader } from 'components/authorizations/authorization-details-header.component';
import { FormStatusDetailsHeader } from 'components/form-statuses/form-status-details-header.component';
import { StatusDefinitionRenderer } from 'components/masonry/status-definition-renderer.component';
import {
  FILTER_NAMESPACE,
  ScfhpSelectInputRenderer,
  setFilterActionHandler,
  clearFiltersActionHandler,
} from 'components/masonry/scfhp-select-input.renderer';
import { ScfhpFilterChipsRenderer } from 'components/masonry/scfhp-filter-chips.renderer';
import { EmptyState } from 'components/empty-state.component';
import {
  PAGINATION_NAMESPACE,
  PaginationLayout,
  setPageActionHandler,
} from 'components/masonry/pagination-layout.renderer';
import { QueryClient, QueryClientProvider } from 'react-query';
import AppRoutes from './components/AppRoutes';
import {
  routesConfig,
  authlessConfig,
  STANDALONE_AUTH_CONFIG,
  STANDALONE_IMPERSONATION_AUTH_CONFIG,
} from './config';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
    },
  },
});

League.overwriteRoutePaths(routesConfig);

wsDriver.nodeRendererRegistry.registerRenderer(
  'your_care_team_stack',
  'scfhp',
  PCPCustomSectionRenderer,
);

driver.nodeRendererRegistry.registerRenderer(
  'activity_stack',
  'health_journey',
  ActivityStackWidgetV2,
);

wsDriver.nodeRendererRegistry.registerRenderer(
  'recent_claims_cards',
  'claims',
  RecentClaimsCardList,
);

wsDriver.nodeRendererRegistry.registerRenderer(
  'contact_info_card',
  'scfhp',
  CustomerServiceCard,
);

wsDriver.nodeRendererRegistry.registerRenderer(
  'carousel',
  'default',
  SuggestedProgramsCarousel,
);

wsDriver.nodeRendererRegistry.registerRenderer(
  'status_banner',
  'scfhp',
  StatusBannerRenderer,
);

wsDriver.nodeRendererRegistry.registerRenderer(
  'access_restriction_banner',
  'scfhp',
  StatusBannerRenderer,
);

// Re-route banner-shaped xs_visual_card widgets through Genesis StatusBanner
// so the home-page warning banners share chrome and icon size. Tile-shaped
// xs_visual_cards delegate back to the platform widget unchanged.
wsDriver.nodeRendererRegistry.overrideRenderer(
  'xs_visual_card',
  'default',
  ScfhpXSVisualCardRenderer,
);

// Masonry's default info banner ships Info Circle Fill; SCFHP standardises
// info banners on Information Circle 2 Filled.
driver.nodeRendererRegistry.overrideRenderer(
  'statusBanner',
  'default',
  ScfhpStatusBannerRenderer,
);

driver.nodeRendererRegistry.registerRenderer(
  'authorizationCard',
  'scfhp_authorizations',
  AuthorizationCard,
);

driver.nodeRendererRegistry.registerRenderer(
  'authorizationsEmptyState',
  'scfhp_authorizations',
  EmptyState,
);

driver.nodeRendererRegistry.registerRenderer(
  'authorizationServiceCard',
  'scfhp_authorization_details',
  AuthorizationServiceCard,
);

driver.nodeRendererRegistry.registerRenderer(
  'authorizationServiceInfoSection',
  'scfhp_authorization_details',
  AuthorizationServiceInfoSection,
);

driver.nodeRendererRegistry.registerRenderer(
  'authorizationDetailsHeader',
  'scfhp_authorization_details',
  AuthorizationDetailsHeader,
);

driver.nodeRendererRegistry.registerRenderer(
  'formStatusDetailsHeader',
  'scfhp_form_status_details',
  FormStatusDetailsHeader,
);

driver.nodeRendererRegistry.registerRenderer(
  'composableCard',
  'scfhp_status_definitions',
  StatusDefinitionRenderer,
);

driver.nodeRendererRegistry.registerRenderer(
  'paginationLayout',
  PAGINATION_NAMESPACE,
  PaginationLayout,
);

// Submitted-requests filter dropdowns: override the stock selectInput
// so user picks commit. See scfhp-select-input.renderer.tsx for the
// snap-back bug and direct-reload rationale.
driver.nodeRendererRegistry.registerRenderer(
  'selectInput',
  'scfhp_form_status_filter',
  ScfhpSelectInputRenderer,
);

// Active-filter chips + Clear All (web). Mounted on a `box` node in the filter
// namespace; see scfhp-filter-chips.renderer.tsx.
driver.nodeRendererRegistry.registerRenderer(
  'box',
  'scfhp_form_status_filter',
  ScfhpFilterChipsRenderer,
);

driver.actionController.registerHandler(
  'setPage',
  PAGINATION_NAMESPACE,
  setPageActionHandler,
);

driver.actionController.registerHandler(
  'setFilter',
  FILTER_NAMESPACE,
  setFilterActionHandler,
);

driver.actionController.registerHandler(
  'clearFilters',
  FILTER_NAMESPACE,
  clearFiltersActionHandler,
);

const getAuthConfig = (): StandaloneAuthInitConfig => {
  let authConfig = localStorage.getItem('AUTH_CONFIG');
  if (!authConfig) {
    localStorage.setItem('AUTH_CONFIG', 'MEMBER');
    authConfig = 'MEMBER';
  }
  if (authConfig === 'IMPERSONATION') {
    return STANDALONE_IMPERSONATION_AUTH_CONFIG;
  }
  return STANDALONE_AUTH_CONFIG;
};

/**
 * AppShell wraps the LeagueProvider and other providers inside the router context.
 * SDK 3.0 requires LeagueProvider to be rendered within a data router context
 * so that the react-router-adapter can access router internals.
 */
const AppShell = () => {
  const { isOffline, isOnline } = useOnlineStatus();

  const { isLoading, config } = useGetLeagueConfig(
    authlessConfig,
    getAuthConfig(),
  );

  if (isLoading) {
    return (
      <GDSThemeProvider theme={santaClaraTheme}>
        <Spinner
          loading
          loadingText="Loading..."
          css={{
            display: 'block',
            width: '100vw',
            height: '100vh',
            alignContent: 'center',
            textAlign: 'center',
          }}
        />
      </GDSThemeProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <League.LeagueProvider config={config}>
        <ErrorBoundary fallback={<ErrorState error={null} />}>
          <Suspense fallback={<LoadingIndicator />}>
            {isOffline && <FullPageError error={null} route="/home" />}
            {isOnline && <Outlet />}
            <ReactQueryDevtools />
          </Suspense>
        </ErrorBoundary>
      </League.LeagueProvider>
    </QueryClientProvider>
  );
};

const router = createBrowserRouter([
  {
    path: '*',
    element: <AppShell />,
    children: [
      {
        path: '*',
        element: <AppRoutes />,
      },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
